import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Textarea } from "@/components/ui/textarea";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { senderDisplayName, type SenderRole } from "@/lib/messageIdentity";

interface MessageRow {
  id: string;
  body: string;
  created_at: string;
  read_at: string | null;
  recipient_id: string;
  sender_id: string;
  thread_id: string;
  sender_role?: SenderRole;
}

export const Route = createFileRoute("/client/messages")({
  head: () => ({ meta: [{ title: "Messages — Family Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <ClientMessages />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function ClientMessages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  const [advisorName, setAdvisorName] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!user) return;
    void loadMessages();
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`client-messages:${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "support_messages", filter: `recipient_id=eq.${user.id}` },
        () => { void loadMessages(); },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "support_messages", filter: `sender_id=eq.${user.id}` },
        () => { void loadMessages(); },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user?.id]);

  async function loadMessages() {
    if (!user) return;
    const { data: client } = await supabase
      .from("clients")
      .select("assigned_agent_id")
      .eq("user_id", user.id)
      .maybeSingle();
    setAdvisorId(client?.assigned_agent_id ?? null);
    if (client?.assigned_agent_id) {
      const { data: advisorProfile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", client.assigned_agent_id)
        .maybeSingle();
      setAdvisorName(advisorProfile?.full_name ?? null);
    }

    const { data } = await supabase
      .from("support_messages")
      .select("id, body, created_at, read_at, recipient_id, sender_id, thread_id, sender_role")
      .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
      .order("created_at", { ascending: true });
    setMessages(data ?? []);
  }

  async function sendMessage() {
    if (!user || !advisorId || !draft.trim()) return;
    const threadId = messages[0]?.thread_id ?? crypto.randomUUID();
    const { error } = await supabase.from("support_messages").insert({
      sender_id: user.id,
      recipient_id: advisorId,
      body: draft.trim(),
      thread_id: threadId,
      // Acting capacity: on this surface the sender is always writing as the
      // household, even if they also hold a staff/admin role at the firm.
      sender_role: "client",
    });
    if (!error) {
      setDraft("");
      await loadMessages();
    }
  }

  const unread = useMemo(() => messages.filter((message) => message.recipient_id === user?.id && !message.read_at).length, [messages, user?.id]);

  return (
    <PortalPage eyebrow="Secure Messaging" title="Messages" intro={`${messages.length} message${messages.length === 1 ? "" : "s"} · ${unread} unread`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft min-h-[360px]">
          {messages.length === 0 ? (
            <p className="font-sans text-[13px] text-charcoal-500">No messages yet.</p>
          ) : (
            <ul className="space-y-4">
              {messages.map((message) => {
                const mine = message.sender_id === user?.id;
                const who = senderDisplayName(
                  message.sender_role,
                  message.sender_id === advisorId ? (advisorName ?? "Your Strategist") : null,
                );
                return (
                  <li key={message.id} className={`max-w-[82%] rounded-[4px] border p-4 ${mine ? "ml-auto bg-cream-50 border-bronze-600/30" : "bg-white border-bronze-600/20"}`}>
                    <p className="mb-1 font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-bronze-600">
                      {mine ? "You" : who}
                    </p>
                    <p className="font-sans text-[14px] text-charcoal-900 leading-relaxed">{message.body}</p>
                    <p className="mt-2 font-sans text-[11px] text-charcoal-500">{new Date(message.created_at).toLocaleString()}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
          <h3 className="font-display text-navy-800 text-[22px]">Send a message</h3>
          <Textarea value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Write to your Strategist" className="mt-4 min-h-32 bg-cream-50" />
          <PrimaryButton onClick={sendMessage} disabled={!advisorId || !draft.trim()} className="mt-4 w-full">Send</PrimaryButton>
          {!advisorId && <p className="mt-3 font-sans text-[12px] text-charcoal-500">A Strategist must be assigned before messages can be sent.</p>}
        </div>
      </div>
    </PortalPage>
  );
}